import type { BundlePdpResponse } from "@halm-club/halm-shared";

export type PdpNavigationStep = NonNullable<
  BundlePdpResponse["navigationSteps"]
>[number];

export type PdpNavigationSteps = PdpNavigationStep[];
export type CartSections = Record<string, string>;

const CART_PATH = "/cart";
const OPEN_CART_DRAWER_EVENT = "halm:open-cart-drawer";
const CHECKOUT_PATH_PATTERN = /\/checkouts?(\/|$)/i;

declare global {
  interface HalmNavigationHistoryEntry {
    readonly url: string;
  }

  interface HalmNavigationActivation {
    readonly from: HalmNavigationHistoryEntry | null;
  }

  interface HalmNavigation {
    readonly activation?: HalmNavigationActivation | null;
  }

  interface Window {
    HalmCartDrawer?: {
      open: (options?: { sections?: CartSections }) => void;
      refresh?: () => void;
    };
    navigation?: HalmNavigation;
  }
}

type OpenCartDrawerEventDetail = {
  handled?: boolean;
  sections?: CartSections;
};

const isCartHref = (href: string) => {
  const url = new URL(href, window.location.origin);

  return url.pathname.replace(/\/$/, "") === CART_PATH;
};

export const openCartDrawer = (sections?: CartSections) => {
  const event = new CustomEvent<OpenCartDrawerEventDetail>(
    OPEN_CART_DRAWER_EVENT,
    {
      detail: { sections },
    },
  );

  window.dispatchEvent(event);

  if (event.detail.handled) {
    return;
  }

  if (window.HalmCartDrawer) {
    window.HalmCartDrawer.open({ sections });
    return;
  }

  window.location.href = CART_PATH;
};

const isCheckoutUrl = (url: string) => {
  try {
    const checkoutUrl = new URL(url, window.location.origin);

    return (
      CHECKOUT_PATH_PATTERN.test(checkoutUrl.pathname) ||
      checkoutUrl.hostname.includes("checkout")
    );
  } catch {
    return false;
  }
};

const isCheckoutNavigationActivation = () => {
  const fromUrl = window.navigation?.activation?.from?.url;

  return fromUrl ? isCheckoutUrl(fromUrl) : false;
};

const isCheckoutReferrer = () => {
  if (!document.referrer) {
    return false;
  }

  return isCheckoutUrl(document.referrer);
};

export const getCurrentNavigationStep = (
  navigationSteps?: PdpNavigationSteps,
) => navigationSteps?.find((step) => step.isCurrentPage);

export const getNextPdpNavigationHref = (
  navigationSteps?: PdpNavigationSteps,
) => {
  if (!navigationSteps?.length) {
    return CART_PATH;
  }

  const currentStepIndex = navigationSteps.findIndex(
    (step) => step.isCurrentPage,
  );

  if (currentStepIndex < 0) {
    return CART_PATH;
  }

  return navigationSteps[currentStepIndex + 1]?.href ?? CART_PATH;
};

export const navigateToNextPdpStepOrCart = (
  navigationSteps?: PdpNavigationSteps,
  options: { cartSections?: CartSections } = {},
) => {
  const href = getNextPdpNavigationHref(navigationSteps);

  if (isCartHref(href)) {
    openCartDrawer(options.cartSections);
    return;
  }

  window.location.href = href;
};

export const openCartDrawerIfReturningFromCheckout = () => {
  if (!isCheckoutNavigationActivation() && !isCheckoutReferrer()) {
    return;
  }

  window.HalmCartDrawer?.open();
};
